import { DataTag } from '@/components/DataTag/DataTag'
import type {
  ClusterBinding,
  LicenseResponseBody,
} from '@/features/Licenses/api/licenseHooks'
import {
  CLIENT_TIMEOUT_CODE,
  CLIENT_TIMEOUT_STATUS,
  invalidateOrgLicenseDetail,
  useRegenerateOrgLicenseBindingConnectionString,
} from '@/features/Licenses/api/licenseHooks'
import { LicenseFormDialog } from '@/features/Licenses/components/LicenseFormDialog'
import { mapMutationError } from '@/features/Licenses/lib/mutationHandlers'
import {
  ERROR_TOAST_OPTION,
  SUCCESS_TOAST_OPTION,
  showOrUpdateToast,
} from '@/hooks/useToast/toastUtils'
import {
  Alert,
  Box,
  Button,
  Field,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

type Props = {
  license: LicenseResponseBody
  binding: ClusterBinding
  onSuccess: () => void
  /**
   * Reports the in-flight state up to whoever owns the `Dialog.Root`. Every
   * status in `INLINE_STATUSES` is deliberately withheld from the toast, so a
   * dialog dismissed mid-request would swallow its own failure — including
   * `500 CONNECTION_STRING_NOT_PERSISTED`, where the old credential is already
   * dead and only the retry offered here repairs it. The owner refuses escape
   * and backdrop while this is `true`; the form refuses cancel and close.
   */
  onPendingChange?: (pending: boolean) => void
}

/* Each of these says something specific about *this* binding or environment
   (400 malformed id, 408 the client's own bound expiring, 409 no agent /
   rotation in flight / no project, 500 CONNECTION_STRING_NOT_PERSISTED, 502
   downstream, 503 Data Intelligence unavailable or outcome unknown), so the
   operator should read it in the dialog where the action still is. `500`
   earns its place because CONNECTION_STRING_NOT_PERSISTED asks for an
   immediate retry, and the retry affordance is right here. Everything else —
   404 BINDING_NOT_FOUND above all — falls through to the toast. */
const INLINE_STATUSES = [400, CLIENT_TIMEOUT_STATUS, 409, 500, 502, 503]

/* The documented problem-detail codes for this endpoint, plus the one the
   fetcher mints itself when its bound expires. Each maps to copy that states
   the operator's response rather than restating the status, which is why the
   code and not the HTTP status selects the message. */
const CODE_KEYS = [
  'BINDING_HAS_NO_AGENT',
  'ROTATION_IN_PROGRESS',
  'ORG_HAS_NO_DATA_INTELLIGENCE_PROJECT',
  'CONNECTION_STRING_NOT_PERSISTED',
  'DATA_INTELLIGENCE_UNAVAILABLE',
  'MINT_OUTCOME_UNKNOWN',
  /* Aborting at the client says nothing about what the server did, so this
     reads as MINT_OUTCOME_UNKNOWN does — outcome unconfirmed, the credential
     may already be rotated — and never as a clean failure. It keeps its own
     key because it also has to explain that *we* stopped waiting. */
  CLIENT_TIMEOUT_CODE,
] as const

type ErrorCode = (typeof CODE_KEYS)[number]

function isKnownCode(code: string | undefined): code is ErrorCode {
  return CODE_KEYS.includes(code as ErrorCode)
}

export function RegenerateConnectionStringForm({
  license,
  binding,
  onSuccess,
  onPendingChange,
}: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const queryClient = useQueryClient()
  const [confirmText, setConfirmText] = useState('')

  const mutation = useRegenerateOrgLicenseBindingConnectionString({
    /* The new string is never returned — there is nothing to reveal or copy, so
       a toast is the whole of the success feedback. */
    onSuccess: () => {
      /* The response is a ClusterBinding, so it cannot be written into the
         license cache: setOrgLicenseQueryData would overwrite the license
         document with a four-field object. And invalidateOrgLicenses skips the
         detail query by design — the only query that renders bindings. */
      invalidateOrgLicenseDetail(queryClient, license.id)
      showOrUpdateToast('regenerate-connection-string-success', {
        ...SUCCESS_TOAST_OPTION,
        title: String(
          t('licenses.actions.regenerateConnectionString.success.title'),
        ),
        description: String(
          t('licenses.actions.regenerateConnectionString.success.description'),
        ),
      })
      onSuccess()
    },
    onError: (error) => {
      if (INLINE_STATUSES.includes(error.status ?? 0)) {
        return
      }
      showOrUpdateToast('regenerate-connection-string-error', {
        ...ERROR_TOAST_OPTION,
        title: String(
          t('licenses.actions.regenerateConnectionString.error.title'),
        ),
        description: String(
          t('licenses.actions.regenerateConnectionString.error.generic'),
        ),
      })
    },
  })

  useEffect(() => {
    onPendingChange?.(mutation.isPending)
  }, [mutation.isPending, onPendingChange])

  const canSubmit = confirmText === 'regenerate'

  /* Layered on top of mapMutationError rather than replacing it: mapMutationError
     still decides inline-vs-toast and owns the correlation-id line, so a status
     it rejects (404, 429) stays null even when the payload carries a code. Only
     a message it already agreed to show gets substituted. */
  const mappedError = mapMutationError(mutation, {
    inlineStatuses: INLINE_STATUSES,
    correlationIdStatuses: [502],
    fallbackDetail: String(
      t('licenses.actions.regenerateConnectionString.error.generic'),
    ),
  })

  /* A 408 does not always come from our own bound expiring: an intermediary
     (nginx, an ALB) mints its own "Request Timeout" with no problem-detail
     code at all, and the lookup below would then miss and leave the operator
     reading that raw statusText as if the rotation had cleanly failed. Both
     origins mean the same thing — we stopped waiting and do not know whether
     the server minted — so both get the CLIENT_TIMEOUT copy. Narrow on
     purpose: only a 408 is rewritten, and a 408 that does carry a known code
     resolves through the normal path. */
  const payloadCode = mutation.error?.payload?.code
  const errorCode =
    mutation.error?.status === CLIENT_TIMEOUT_STATUS &&
    !isKnownCode(payloadCode)
      ? CLIENT_TIMEOUT_CODE
      : payloadCode
  const inDialogError =
    mappedError && isKnownCode(errorCode)
      ? {
          ...mappedError,
          detail: String(
            t(
              `licenses.actions.regenerateConnectionString.error.codes.${errorCode}`,
            ),
          ),
        }
      : mappedError

  const handleSubmit = () => {
    if (canSubmit) {
      mutation.mutate({
        pathParams: {
          orgId: license.orgId,
          licenseId: license.id,
          clusterId: binding.clusterId,
        },
      })
    }
  }

  return (
    <LicenseFormDialog
      title={String(t('licenses.actions.regenerateConnectionString.title'))}
      errorTitle={String(
        t('licenses.actions.regenerateConnectionString.error.title'),
      )}
      correlationIdLabel={String(
        t('licenses.actions.regenerateConnectionString.error.correlationId'),
      )}
      inDialogError={inDialogError}
      cancelLabel={String(
        t('licenses.actions.regenerateConnectionString.cancel'),
      )}
      dismissDisabled={mutation.isPending}
      submitButton={
        <Button
          colorPalette="red"
          disabled={!canSubmit || mutation.isPending}
          loading={mutation.isPending}
          onClick={handleSubmit}
        >
          {String(
            t('licenses.actions.regenerateConnectionString.submitButton'),
          )}
        </Button>
      }
    >
      <Text fontSize="sm">
        {String(t('licenses.actions.regenerateConnectionString.warning'))}
      </Text>
      {/* Unconditional, unlike RevokeForm's PLG-only Stripe alert: the broker
          on this cluster disconnects whatever the license looks like, and it is
          the consequence the operator is least likely to have anticipated. */}
      <Alert.Root status="warning">
        <Alert.Indicator />
        <Alert.Content>
          <Alert.Description>
            {String(
              t(
                'licenses.actions.regenerateConnectionString.brokerDisconnectWarning',
              ),
            )}
          </Alert.Description>
        </Alert.Content>
      </Alert.Root>
      <VStack align="start" gap={1}>
        <Text fontSize="xs" color="text.text-subtle">
          {String(
            t('licenses.actions.regenerateConnectionString.identityLabel'),
          )}
        </Text>
        {/* The cluster, not the license: the credential belongs to this one
            binding. No copy button, same reasoning as RevokeForm — the dialog
            asks for the literal word `regenerate`, not the cluster id. */}
        <DataTag value={binding.clusterId} hasCopy={false}>
          {binding.clusterId}
        </DataTag>
      </VStack>
      <Field.Root>
        <Field.Label>
          <Trans
            i18nKey="licenses.actions.regenerateConnectionString.typeToConfirmLabel"
            t={t}
            components={{
              word: <Box as="strong" fontFamily="mono" />,
            }}
          />
        </Field.Label>
        <Input
          placeholder={String(
            t(
              'licenses.actions.regenerateConnectionString.typeToConfirmPlaceholder',
            ),
          )}
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          borderColor={canSubmit ? 'green.400' : undefined}
          /* Locked with everything else in flight. Clearing the field during
             the 45s bound would disable the inline retry, which on a
             CONNECTION_STRING_NOT_PERSISTED is the only repair for an already
             dead credential. */
          disabled={mutation.isPending}
        />
      </Field.Root>
    </LicenseFormDialog>
  )
}
